import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { 
  CalendarIcon, 
  Loader2, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Truck, 
  Building2, 
  Download, 
  Filter,
  BarChart3,
  PieChartIcon,
  RefreshCw,
  Target,
  Clock,
  CheckCircle
} from 'lucide-react';
import TenantFilter from '../components/TenantFilter';

const COLORS = ['#0F172A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#F97316', '#64748B'];

const IndicatorsPage = () => {
  const { token } = useAuth();
  const { selectedTenant, getTenantParam } = useTenant();
  const [data, setData] = useState(null);
  const [missions, setMissions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros avançados
  const [daysFilter, setDaysFilter] = useState(90);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, [token, selectedTenant]);

  const fetchData = async () => {
    setLoading(true);
    const tenantParam = getTenantParam();
    const separator = tenantParam ? '&' : '?';
    try {
      const [indicatorsData, missionsData, customersData, suppliersData] = await Promise.all([
        api.get(`/indicators${tenantParam}`, token),
        api.get(`/missions${tenantParam}`, token),
        api.get(`/customers${tenantParam}`, token),
        api.get(`/suppliers${tenantParam}`, token)
      ]);
      setData(indicatorsData);
      setMissions(missionsData);
      setCustomers(customersData);
      setSuppliers(suppliersData);
    } catch (error) {
      toast.error('Erro ao carregar indicadores');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar missões
  const filteredMissions = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysFilter);
    
    return missions.filter(m => {
      const missionDate = new Date(m.created_at);
      if (missionDate < cutoff) return false;
      if (customerFilter !== 'all' && m.customer_id !== customerFilter) return false;
      if (supplierFilter !== 'all' && m.supplier_id !== supplierFilter) return false;
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      return true;
    });
  }, [missions, daysFilter, customerFilter, supplierFilter, statusFilter]);

  // Calcular métricas filtradas
  const metrics = useMemo(() => {
    const total = filteredMissions.length;
    const finalizadas = filteredMissions.filter(m => m.status === 'finalizada' || m.status === 'faturado').length;
    const emAndamento = filteredMissions.filter(m => m.status === 'em_andamento').length;
    const canceladas = filteredMissions.filter(m => m.status === 'cancelada').length;
    const sla = total > 0 ? Math.round((finalizadas / total) * 100) : 0;
    
    // Missões por mês (filtradas)
    const byMonth = {};
    filteredMissions.forEach(m => {
      const month = new Date(m.created_at).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      byMonth[month] = (byMonth[month] || 0) + 1;
    });
    const monthlyData = Object.entries(byMonth).map(([month, count]) => ({ month, count })).slice(-6);

    // Por motivo
    const byReason = {};
    filteredMissions.forEach(m => {
      const reason = m.reason_description || 'Outros';
      byReason[reason] = (byReason[reason] || 0) + 1;
    });
    const reasonData = Object.entries(byReason)
      .map(([reason, count]) => ({ reason: reason.substring(0, 20), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // Por fornecedor
    const bySupplier = {};
    filteredMissions.forEach(m => {
      const supplier = m.supplier_name || 'Não atribuído';
      bySupplier[supplier] = (bySupplier[supplier] || 0) + 1;
    });
    const supplierData = Object.entries(bySupplier)
      .map(([supplier, count]) => ({ supplier, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // Por cliente
    const byCustomer = {};
    filteredMissions.forEach(m => {
      const customer = m.customer_name || 'Não informado';
      byCustomer[customer] = (byCustomer[customer] || 0) + 1;
    });
    const customerData = Object.entries(byCustomer)
      .map(([customer, count]) => ({ customer, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    return { total, finalizadas, emAndamento, canceladas, sla, monthlyData, reasonData, supplierData, customerData };
  }, [filteredMissions]);

  const exportToCSV = () => {
    const headers = ['Mês,Missões,Finalizadas,Canceladas'];
    const rows = metrics.monthlyData.map(d => `${d.month},${d.count},0,0`);
    const csv = [...headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `indicadores_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Relatório exportado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3">
          <p className="font-medium text-slate-900 text-sm">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm font-mono">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="indicators-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Indicadores
          </h1>
          <p className="text-slate-500 mt-1">Análise de performance operacional</p>
        </div>
        <div className="flex items-center gap-2">
          <TenantFilter />
          <Button variant="outline" onClick={fetchData} className="rounded-xl">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={exportToCSV} className="bg-slate-900 hover:bg-slate-800 rounded-xl">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Filtros Avançados */}
      <Card className="premium-card border-0">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Período:</span>
              <Input
                type="number"
                min="7"
                max="365"
                value={daysFilter}
                onChange={(e) => setDaysFilter(parseInt(e.target.value) || 90)}
                className="w-20 h-8 text-center rounded-lg border-slate-200"
                data-testid="days-filter"
              />
              <span className="text-sm text-slate-500">dias</span>
            </div>

            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Clientes</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Truck className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Fornecedor" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Fornecedores</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-10 rounded-xl border-slate-200 bg-slate-50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="criada">Criadas</SelectItem>
                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                <SelectItem value="finalizada">Finalizadas</SelectItem>
                <SelectItem value="cancelada">Canceladas</SelectItem>
              </SelectContent>
            </Select>

            {(customerFilter !== 'all' || supplierFilter !== 'all' || statusFilter !== 'all') && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setCustomerFilter('all');
                  setSupplierFilter('all');
                  setStatusFilter('all');
                }}
                className="text-slate-500"
              >
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="premium-card border-0 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Total de Missões</p>
                <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>{metrics.total}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Finalizadas</p>
                <p className="text-3xl font-bold text-green-600 mt-1" style={{ fontFamily: 'JetBrains Mono' }}>{metrics.finalizadas}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Em Andamento</p>
                <p className="text-3xl font-bold text-blue-600 mt-1" style={{ fontFamily: 'JetBrains Mono' }}>{metrics.emAndamento}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Taxa de Sucesso</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'JetBrains Mono' }}>{metrics.sla}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Missions by Month - Area Chart */}
        <Card className="lg:col-span-2 premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Evolução de Missões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={metrics.monthlyData}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0F172A" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0F172A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" stroke="#0F172A" strokeWidth={2} fill="url(#colorCount)" name="Missões" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Missions by Reason - Donut Chart */}
        <Card className="premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <PieChartIcon className="w-5 h-5 text-purple-500" />
              Por Motivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={metrics.reasonData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="reason"
                  >
                    {metrics.reasonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Missions by Supplier */}
        <Card className="premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <Truck className="w-5 h-5 text-orange-500" />
              Por Fornecedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.supplierData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    dataKey="supplier" 
                    type="category" 
                    tick={{ fontSize: 11 }} 
                    width={100}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#F97316" radius={[0, 4, 4, 0]} name="Missões" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Missions by Customer */}
        <Card className="premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <Building2 className="w-5 h-5 text-blue-500" />
              Por Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.customerData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    dataKey="customer" 
                    type="category" 
                    tick={{ fontSize: 11 }} 
                    width={100}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Missões" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Performance Table */}
        <Card className="premium-card border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <TrendingUp className="w-5 h-5 text-green-500" />
              Performance por Fornecedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-72">
              <table className="data-table text-sm">
                <thead>
                  <tr>
                    <th>Fornecedor</th>
                    <th className="text-right">Missões</th>
                    <th className="text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.supplierData.map((item, index) => (
                    <tr key={index}>
                      <td className="font-medium">{item.supplier}</td>
                      <td className="text-right font-mono">{item.count}</td>
                      <td className="text-right">
                        <Badge className="bg-slate-100 text-slate-600 font-mono">
                          {metrics.total > 0 ? Math.round((item.count / metrics.total) * 100) : 0}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IndicatorsPage;
